/**
 * ReferenceCard — one ledger row (the mock's `.rcard`). Renders any of the seven card states, with
 * the single elevated (gold) Ready·Locked style, the muted Declined style, and a chip + dot that
 * always carry a text label (never colour alone — design-spec hard rule 3).
 *
 * Web adaptation of the design-spec/05 phone row (DESIGN-NOTES delta d):
 *  - Non-declined rows are a single whole-card link to the request detail (WP-F8b); the accessible
 *    name is "{referee} — {status}". The Ready row's tap opens the unlock sheet, the Letter row's
 *    the viewer — both live behind that same detail route in F8b.
 *  - The Declined row is not a link; its one action is the "ask another ›" link to a fresh request.
 *
 * Chip/dot/meta colours come from `ledger.ts` (pure) + the TEXT-tier tokens: the design mock's
 * chips used the fill-tier `*-deep` on tints (3.47–4.07:1, fails AA) and white on the gold chip
 * (2.24:1); this uses `*-text` on tints and `ink` on the gold fill, per the 16 Jul 2026 amendment.
 */
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  avatarKindFor,
  chipLabelFor,
  initialsOf,
  isElevated,
  metaFor,
  type DotKind,
  type LedgerCard,
  type LedgerCardState,
} from '@/lib/ledger';
import { Avatar } from './Avatar';

/** Chip fill + text classes per state (TEXT-tier tokens; gold fill takes `ink`). */
const CHIP_CLASS: Record<LedgerCardState, string> = {
  sent: 'bg-paper-2 text-ink-quiet',
  opened: 'bg-sage-tint text-sage-text',
  in_progress: 'bg-coral-tint text-coral-text',
  ready: 'bg-gradient-to-br from-[#E7B45C] to-gold text-ink',
  unlocked: 'bg-sage-tint text-sage-text',
  declined: 'bg-muted-tint text-ink-quiet',
  expired: 'bg-paper-2 text-ink-quiet',
};

const DOT_CLASS: Record<Exclude<DotKind, 'none'>, string> = {
  wait: 'bg-ink-38',
  open: 'bg-sage-deep',
  live: 'dot-live bg-coral shadow-[0_0_0_3px_#F7E2D7]',
  done: 'bg-sage',
};

function LockGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" width={11} height={11} aria-hidden="true">
      <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
      <path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function StatusChip({ state }: { state: LedgerCardState }) {
  return (
    <span
      className={cn(
        'inline-flex flex-none items-center gap-1.5 rounded-pill px-2.5 py-1 font-display text-[11px] font-semibold',
        CHIP_CLASS[state],
      )}
    >
      {state === 'ready' && <LockGlyph />}
      {chipLabelFor(state)}
    </span>
  );
}

export function ReferenceCard({ card }: { card: LedgerCard }) {
  const meta = metaFor(card);
  const chipLabel = chipLabelFor(card.state);
  const avatar = <Avatar initials={initialsOf(card.refereeName)} kind={avatarKindFor(card.state)} />;

  const base = 'flex items-center gap-3 rounded-[16px] border px-3 py-2.5 shadow-sm';

  if (card.state === 'declined') {
    // Terminal + candidate-facing: the card reads muted, but the two elements that must stay legible
    // and actionable — the referee's name and the "ask another" link — sit OUTSIDE the dimming, at
    // full contrast. Only the decorative avatar and status chip carry the opacity. (A whole-card
    // opacity-0.62 dropped the interactive link to ~2.4:1, below AA.)
    return (
      <div className={cn(base, 'border-line bg-surface')}>
        <span className="opacity-[0.62]">{avatar}</span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-[14px] font-semibold text-ink">
            {card.refereeName}
          </span>
          <span className="mt-0.5 flex items-center justify-between gap-2">
            <Link to="/request" className="font-medium text-coral-text underline-offset-2 hover:underline">
              ask another ›
            </Link>
            <span className="opacity-[0.62]">
              <StatusChip state="declined" />
            </span>
          </span>
        </span>
      </div>
    );
  }

  const body = (
    <>
      {avatar}
      <span className="min-w-0 flex-1">
        <span className="block truncate font-display text-[14px] font-semibold text-ink">
          {card.refereeName}
        </span>
        <span className="mt-0.5 flex items-center justify-between gap-2">
          <span className="flex min-w-0 flex-1 items-center gap-1.5 truncate text-[12px] text-ink-quiet">
            {meta.dot !== 'none' && (
              <span className={cn('h-[7px] w-[7px] flex-none rounded-full', DOT_CLASS[meta.dot])} />
            )}
            <span className="truncate">{meta.text}</span>
          </span>
          <StatusChip state={card.state} />
        </span>
      </span>
    </>
  );

  return (
    <Link
      to={`/requests/${card.id}`}
      aria-label={`${card.refereeName} — ${chipLabel}`}
      className={cn(
        base,
        'transition-[background-color,box-shadow,filter] duration-ui ease-thread',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-deep/25 focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
        isElevated(card.state)
          ? // The one elevated moment: warm gold gradient card + gold glow (mock `.rcard--ready`).
            'border-[rgba(200,137,42,0.4)] bg-gradient-to-b from-[#FEFBF3] to-[#FBF4E4] shadow-ready hover:brightness-[1.02]'
          : 'border-line bg-surface hover:bg-surface-tint',
      )}
    >
      {body}
    </Link>
  );
}
